import { useEffect, useMemo, useRef, useState } from "react";
import {
  admin, type LogCategory, type LogHit, type LogQuery, type LogSearch,
} from "../../api/admin";
import { when, Linkified } from "./bits";

const PAGE = 100;

/**
 * The whole log, searched.
 *
 * The run drawer answers "what is this run doing". This answers the question
 * that used to mean opening thirty drawers and reading: when did that last
 * happen, and to which records. Free text runs against the message *and* its
 * fields, so half a release key finds every line about that record across
 * every run there has ever been.
 *
 * Nothing polls. A search is a question asked at a moment, and a result set
 * that reshuffles under the cursor while you read it is worse than a stale one.
 */
export function Logs({ onWatch }: { onWatch: (id: number) => void }) {
  const [cats, setCats] = useState<LogCategory[]>([]);
  const [kinds, setKinds] = useState<string[]>([]);

  const [text, setText] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [level, setLevel] = useState("");
  const [kind, setKind] = useState("");
  const [since, setSince] = useState("");
  const [order, setOrder] = useState<"time" | "category">("time");
  const [offset, setOffset] = useState(0);

  const [res, setRes] = useState<LogSearch | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    admin.logCategories().then(setCats).catch(() => { /* the filter degrades to free text */ });
    admin.jobs().then((js) => setKinds(js.map((j) => j.kind))).catch(() => { /* same */ });
  }, []);

  // The typed query is debounced; everything else searches at once, because a
  // tick is a decision and a keystroke is not.
  const debounced = useDebounced(text, 300);
  const query: LogQuery = useMemo(() => ({
    q: debounced || undefined,
    category: picked.length ? picked : undefined,
    level: level ? [level] : undefined,
    kind: kind ? [kind] : undefined,
    since: since || undefined,
    order,
    limit: PAGE,
    offset,
  }), [debounced, picked, level, kind, since, order, offset]);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    admin.logs(query)
      .then((r) => { if (alive) { setRes(r); setError(null); } })
      .catch((e: Error) => alive && setError(e.message))
      .finally(() => alive && setBusy(false));
    return () => { alive = false; };
  }, [query]);

  // Any change of filter is a different question, so it starts at page one.
  const reset = <T,>(set: (v: T) => void) => (v: T) => { setOffset(0); set(v); };

  const byName = useMemo(
    () => new Map(cats.map((c) => [c.name, c])), [cats]);
  const filtered = picked.length > 0 || level || kind || since || debounced;

  return (
    <>
      <div className="controls logsearch">
        <input
          type="search"
          className="logfind"
          placeholder="Anything in a message or its fields — a release key, a label, an error"
          value={text}
          onChange={(e) => reset(setText)(e.target.value)}
        />
        <CategoryPicker categories={cats} selected={picked} onChange={reset(setPicked)} />
        <select value={level} onChange={(e) => reset(setLevel)(e.target.value)}>
          <option value="">Any level</option>
          {["error", "warn", "info"].map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={kind} onChange={(e) => reset(setKind)(e.target.value)}>
          <option value="">Any job</option>
          {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <input
          type="date"
          title="Only lines written on or after this day"
          value={since}
          onChange={(e) => reset(setSince)(e.target.value)}
        />
        <select value={order} onChange={(e) => reset(setOrder)(e.target.value as "time" | "category")}>
          <option value="time">Newest first</option>
          <option value="category">By category</option>
        </select>
        {filtered && (
          <button className="link" onClick={() => {
            setText(""); setPicked([]); setLevel(""); setKind(""); setSince(""); setOffset(0);
          }}>Clear</button>
        )}
        <span className="count">
          {busy && <span className="spinner" />}
          {res && `${res.total.toLocaleString()}${res.capped ? "+" : ""} lines`}
        </span>
      </div>

      {error && <div className="error">{error}</div>}
      {res && res.lines.length === 0 && !busy && (
        <div className="empty">Nothing in the log matches that.</div>
      )}

      {res && res.lines.length > 0 && (
        <table className="runs loglist">
          <thead>
            <tr>
              <th>when</th><th>category</th><th>job</th><th>run</th><th>what happened</th>
            </tr>
          </thead>
          <tbody>
            {res.lines.map((l) => (
              <Line key={l.id} line={l} info={byName.get(l.category)} onWatch={onWatch}
                    onCategory={() => reset(setPicked)([l.category])} />
            ))}
          </tbody>
        </table>
      )}

      {res && (res.offset > 0 || res.lines.length === PAGE) && (
        <div className="more">
          <button disabled={offset === 0 || busy}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            ← newer
          </button>
          <span className="dim small pager">
            {(res.offset + 1).toLocaleString()}–{(res.offset + res.lines.length).toLocaleString()}
          </span>
          <button disabled={res.lines.length < PAGE || busy}
                  onClick={() => setOffset(offset + PAGE)}>
            older →
          </button>
        </div>
      )}
    </>
  );
}

function Line({ line, info, onWatch, onCategory }: {
  line: LogHit;
  info?: LogCategory;
  onWatch: (id: number) => void;
  onCategory: () => void;
}) {
  return (
    <tr className={`line ${line.level}`}>
      <td className="dim small nowrap" title={new Date(line.at).toLocaleString()}>
        {when(line.at)}
      </td>
      <td>
        <button
          className={`cat ${line.level}`}
          title={info?.help ?? info?.label ?? "written before this category existed"}
          onClick={onCategory}
        >
          {line.category}
        </button>
      </td>
      <td><code>{line.kind}</code></td>
      <td className="num">
        <button className="link" onClick={() => onWatch(line.runId)}>{line.runId}</button>
      </td>
      <td>
        <span className="m"><Linkified text={line.msg} /></span>
        {line.fields && (
          <span className="f">
            {Object.entries(line.fields).map(([k, v]) => (
              <span key={k}>{k}=<b><Linkified text={String(v)} /></b></span>
            ))}
          </span>
        )}
      </td>
    </tr>
  );
}

/**
 * The category filter: a dropdown of checkboxes, grouped as the vocabulary
 * groups them and carrying each one's count.
 *
 * A count of zero is shown rather than hidden. "No line has ever been written
 * under ANALYSIS_FAILED" is a useful thing to learn from a filter, and a list
 * that quietly drops it makes the reader wonder whether they misremembered
 * the name.
 */
function CategoryPicker({ categories, selected, onChange }: {
  categories: LogCategory[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [find, setFind] = useState("");
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const needle = find.trim().toLowerCase();
  const groups = useMemo(() => {
    const out = new Map<string, LogCategory[]>();
    for (const c of categories) {
      if (needle && !c.name.toLowerCase().includes(needle)
        && !c.label.toLowerCase().includes(needle) && !selected.includes(c.name)) continue;
      out.set(c.group, [...(out.get(c.group) ?? []), c]);
    }
    return [...out];
  }, [categories, needle, selected]);

  const label = selected.length === 0 ? "Any category"
    : selected.length === 1 ? selected[0]!
      : `${selected.length} categories`;

  return (
    <div className={`ms tags${open ? " open" : ""}${selected.length ? " on" : ""}`} ref={box}>
      <button type="button" className="ms-btn" onClick={() => setOpen((v) => !v)}>{label}</button>
      <div className="ms-menu wide">
        <div className="ms-tools">
          <input type="search" placeholder="Find a category" value={find}
                 onChange={(e) => setFind(e.target.value)} />
          <button type="button" onClick={() => { onChange([]); setFind(""); }}>Clear</button>
        </div>
        <div className="ms-list">
          {groups.map(([group, cs]) => (
            <div key={group} className="ms-group">
              <div className="ms-grouphd">{group}</div>
              {cs.map((c) => (
                <label key={c.name} title={c.help ?? c.label}>
                  <input
                    type="checkbox"
                    checked={selected.includes(c.name)}
                    onChange={() => onChange(
                      selected.includes(c.name)
                        ? selected.filter((v) => v !== c.name)
                        : [...selected, c.name],
                    )}
                  />
                  <span className={`cat ${c.level}`}>{c.name}</span>
                  <em className="dim small">{c.count.toLocaleString()}</em>
                </label>
              ))}
            </div>
          ))}
          {groups.length === 0 && <div className="ms-empty">Nothing matches that.</div>}
        </div>
      </div>
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
